<template lang="pug">
  .zendesk-widget-container
    transition(name="fade")
      button(v-if="showButtonCorner" @click="mix_onWidgetButtonClick" @mouseenter="mix_loadZenDesk" @touchstart="mix_loadZenDesk" data-mr-ass="widget-button")
        transition-group(name="fade")
          .loader-container(v-if="mix_showLoader" key="loader" data-mr-ass="widget-button-loader" aria-label="loading colorist chat" aria-live="polite")
            .loader(class="mr-5")
            span(class="progress" data-mr-ass="widget-button-progress") {{displayLoadingProgress}}%
          .ask-a-colorist-container(v-else key="loader" data-mr-ass="widget-button-text" aria-label="ask a colorist chat")
            img(class="icon" src="/images/comment.svg" alt="chat icon")
            span(class="colorist-text ml-10") ASK A COLORIST

</template>

<script>
/**
 * An overlay button in the bottom right of the page for opening Zendesk's web widget.
 * Zendesk's (heavy) script is lazy loaded, loaded immediately (needed for proactive chatting) or not loaded at all,
 * depending on the current route/path, for performance reasons.
 */

import openChatMixin from "@mixins/openChatMixin";

export default {
  name: "ZendeskWidget",

  mixins: [
    openChatMixin
  ],

  inject: ['getObjProperty'],

  data() {
    return {
      fetchingButtonInterval: null,
      focusJailInterval: null,
      fetchingContentsInterval: null,
      videoConsultationData: {
        title: 'Free Video Consultation',
        rootStyle: "border-bottom-left-radius: unset !important; border-bottom-right-radius: unset !important",
        listenToEvents: ['click', 'touchstart'],
        mrEventName: 'video consult chat widget',
        targetUrl: '/schedule-video-chat',
        svg: '<svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M8 13V72L18.6667 60.1481H67C69.7614 60.1481 72 57.9096 72 55.1482V13C72 10.2386 69.7614 8 67 8H13C10.2386 8 8 10.2386 8 13Z" stroke="#8F959B" stroke-width="4"/><rect x="21" y="24" width="28" height="22" fill="#8F959B"/><path d="M59 43V28.3333V27L51 35L59 43Z" fill="#8F959B"/></svg>',
      },
      hasLoadedInitially: false,
    };
  },

  watch: {
    // Watches the route changes for setting the load mode dynamically
    $route (to, from) {
      if (to.path !== from.path) {
        if (this.hasLoadedInitially) {
          this.mix_setLoadingMode();
        }
        this.mix_updateAnswerBotSuggestions();
      }
    }
  },

  /**
   * Mounted life-cycle hook
   */
  mounted() {
    this.mix_initialize().then(() => this.hasLoadedInitially = true);
  },


  /** 
   * Cleaning up all relevant variables
   */
  beforeDestroy() {
    clearInterval(this.fetchingButtonInterval);
    clearInterval(this.focusJailInterval);
    clearInterval(this.fetchingContentsInterval);
  },

  methods: {
    /**
     * After the widget is shown it tries to get the button for contact options and attach a click and touch event to it.
     * It keeps trying until it finds the component or until 50 tries. After that the interval handler is cleared.
     * 
     * IMPORTANT: The widget will be destroyed (the iframe) but the chat history will remain. However, the old messages
     * that where modified will have to be reworked again because they will appear without modifications.
     */
    mix_afterWidgetShow() {
      this.configureTextareaInterval();
      this.focusJailInterval = setInterval(() => {
        let internalFrame = document.getElementById('webWidget');

        if (internalFrame) {
          let element = internalFrame.contentWindow.document.querySelector("[data-garden-container-id='focusjail']");

          if (element) {
            element.addEventListener('click', this.onBackButtonClick.bind(this));

            clearInterval(this.focusJailInterval);
          }
        }
      }, 300);
    },

    /**
     * After the widget is hidden it clears the interval if it's running.
     */
    mix_afterWidgetHide() {
      clearInterval(this.fetchingButtonInterval);
      this.fetchingButtonInterval = null;
    },

    /**
     * On click on the widget, I check if that was a click in the back button for a suggestion and if so, I need to
     * refresh items in chat to ensure consistency of the content.
     */
    onBackButtonClick(event) {
      let ariaLabel = this.getObjProperty(event, 'target.parentNode.parentNode.ariaLabel');

      if (ariaLabel === "Back") {
        this.afterGetInTouchClick();
        this.configureTextareaInterval();
      }
    },

    /**
     * on user input in textarea listener, that triggers the relevant intervals until the "Get in touch" button appears. 
     */
    onUserInput(event) {
      if (event.keyCode == 13 && !event.shiftKey) {
        this.createButtonInterval();
      }

      return true;
    },

    /**
     * After widget showing in the UI, or pressing the backbutton in an suggestion from the BOT, this interval
     * searches for the textarea and adds event listeners for when the user inputs texts in case the button
     * for getting in touch appears again (which normally does).
     */
    configureTextareaInterval() {
      let attempts = 0;
      let previousButtonsProcessed = false;

      let fetchingInputInterval = setInterval(() => {
        let internalFrame = document.getElementById('webWidget');

        if (internalFrame) {
          if (!previousButtonsProcessed) {
            this.processButtons();
            previousButtonsProcessed = true;
          }

          let element = internalFrame.contentWindow.document.querySelector("[data-garden-id='forms.textarea']");

          if (element) {
            element.addEventListener('keydown', this.onUserInput.bind(this));

            clearInterval(fetchingInputInterval);

            this.createButtonInterval();
          } else {
            attempts++;
            if (attempts === 50) {
              clearInterval(fetchingInputInterval);
            }
          }
        }
      }, 300);
    },

    /**
     * After the widget is shown it tries to get the button for contact options and attach a click and touch event to it.
     * It keeps trying until it finds the component or until 50 tries. After that the interval handler is cleared.
     */
    createButtonInterval() {
      let element = null;
      let attempts = 0;

      if (this.fetchingButtonInterval) {
        clearInterval(this.fetchingButtonInterval);
      }

      this.fetchingButtonInterval = setInterval(() => {
        let internalFrame = document.getElementById('webWidget');

        if (internalFrame) {
          element = internalFrame.contentWindow.document.querySelector("[data-garden-id='buttons.button']");

          if (element) {
            element.addEventListener('click', this.afterGetInTouchClick);
            element.addEventListener('touchstart', this.afterGetInTouchClick);

            clearInterval(this.fetchingButtonInterval);
          } else {
            attempts++;
            if (attempts === 80) {
              clearInterval(this.fetchingButtonInterval);
            }
          }
        }
      }, 300);
    },

    /**
     * Once the button for get in touch options is clicked I wait until the options are about to shown and add forcefully the new option.
     * It keeps trying until it finds the component or until 50 tries. After that the interval handler is cleared.
     * We don't remove the event handlers for the button because the button itself will be removed from the widget.
     */
    afterGetInTouchClick() {
      let attempts = 0;

      this.fetchingContentsInterval = setInterval(() => {

        if (this.processButtons().length) {
          clearInterval(this.fetchingContentsInterval);
        } else {
          attempts++;
          if (attempts === 50) {
            clearInterval(this.fetchingContentsInterval);
          }
        }
        
      }, 300);
    },

    /**
     * Process all the buttons that are sibilings of "Call us" button and inject a new button for video consultation.
     * If the button was already added it does not adds anything.
     */
    processButtons() {
      let internalFrame = document.getElementById('webWidget');
      if (internalFrame) {
        let elements = internalFrame.contentWindow.document.querySelectorAll("[tabindex='0']");
        elements = [...elements].filter(el => {
          let isCallUs = el.innerText.includes('Call us');
          let isNotVideoConsultationSibiling = !el.parentNode.parentNode.textContent.includes(this.videoConsultationData.title);
          return isCallUs && isNotVideoConsultationSibiling;
        });
        elements.forEach(element => {
          let originalRoot = element.parentNode;

          let rootElement = this.createVideoConsultationButtonFrom(originalRoot);
          originalRoot.parentNode.insertBefore(rootElement, originalRoot.parentNode.childNodes[1]);
        });

        return elements;
      } else {
        return [];
      }
      
    },

    /**
     * It clones the received parameter and sets up all its relevant data with the setup we have for videoconsultation.
     */
    createVideoConsultationButtonFrom(originalElement) {
      let rootElement = originalElement.cloneNode(true);
      rootElement.style = this.videoConsultationData.rootStyle;
      let buttonElement = rootElement.firstChild;

      let trackAndRedirectCallback = this.trackMREventAndRedirect.bind(this, this.videoConsultationData.mrEventName, this.videoConsultationData.targetUrl); // TODO: validate
      this.videoConsultationData.listenToEvents.forEach(eventName => buttonElement.addEventListener(eventName, trackAndRedirectCallback));

      buttonElement.firstChild.innerHTML = this.videoConsultationData.svg;
      buttonElement.lastChild.innerHTML = this.videoConsultationData.title;

      return rootElement;
    },
  },
};
</script>

<style lang="stylus" scoped>

/** Widget button **/
button
  position fixed
  width 172px
  height 46px
  bottom 0
  right 0
  margin: 15px;
  padding 12px
  border 0
  z-index 999998
  cursor pointer
  border none
  border-radius 100px
  background-color brand-color-1

.icon
  width 18px
  height 18px

.ask-a-colorist-container
  display flex
  align-items center
  justify-content center

.colorist-text
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.33px;
  color: #ffffff;

@media mq-mobile
  button
    height 50px
    width 50px
    margin 10px 20px

  .colorist-text
    display none

  .loader
    display none

/** Animations **/
.fade-enter-active,
.fade-leave-active
  transition opacity .5s

.fade-enter,
.fade-leave-to
  opacity 0

/** Loader **/
.loader-container
  display flex
  align-items center
  justify-content center
  transition opacity 0.3s

.progress
  position: relative
  left 5px
  color #ffffff
  transition color 0.3s, opacity 0.3s

$loader-size = 0.5em

.loader,
.loader:after
  border-radius 50%
  width 2.5em;
  height 2.5em;
  margin-top -1px
  margin-left 7px

.loader
  transition all 0.3s
  font-size 10px
  position relative
  text-indent -9999em
  border-top $loader-size solid rgba(255, 255, 255, 0.2)
  border-right $loader-size solid rgba(255, 255, 255, 0.2)
  border-bottom $loader-size solid rgba(255, 255, 255, 0.2)
  border-left $loader-size solid #ffffff
  transform translateZ(0)
  animation loader-animation 0.5s infinite linear

@keyframes loader-animation
  0%
    transform rotate(0deg)

  100%
    transform rotate(360deg)

</style>